import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import Logo from "@/components/Logo";
import { useTheme } from "@/lib/theme";
import { API } from "@/lib/api";
import { redirectTop } from "@/lib/oauth";
import { toast } from "sonner";

// --- Access control (invite-only, Microsoft-first org) ---
// Code is intentionally KEPT, just hidden. Flip these to re-enable later.
const ALLOW_SIGNUP = false; // hide self-service "Create account" (invite-only)
const SHOW_GOOGLE = false;  // hide Google sign-in (Microsoft-first org)
// Microsoft is the primary org sign-in — always offer it on the login page so
// a missing/late /auth/providers response (or env hiccup) can never hide it.
const SHOW_MICROSOFT = true;

export default function Login() {
  const { login, register } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [assistantName, setAssistantName] = useState("Andre");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search.includes("ms_error=1")) {
      toast.error("Couldn't complete Microsoft sign-in. Please try again.");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register({ email, password, name: name || email.split("@")[0], assistant_name: assistantName || "Andre" });
        toast.success("Welcome. Your second brain is being seeded.");
      }
      navigate("/chat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoft = () => {
    // Microsoft refuses to be iframed — navigate the top window.
    redirectTop(`${API}/auth/microsoft/login`);
  };

  return (
    <div className="ph-shell" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Theme toggle — top-right */}
      <button
        onClick={toggleTheme}
        data-testid="login-theme-toggle"
        className="btn btn-ghost"
        title={theme === "light" ? "Switch to dark" : "Switch to light"}
        aria-label="Toggle theme"
        style={{ position: "absolute", top: 18, right: 18, padding: 10 }}
      >
        {theme === "light"
          ? <Moon size={17} strokeWidth={2} />
          : <Sun size={17} strokeWidth={2} />}
      </button>

      <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="auth-card fade-up" data-testid="auth-card">
          <div style={{ marginBottom: 28, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Logo height={56} />
            <div className="text-dim" style={{ fontSize: 13, marginTop: 14 }}>
              Athlete safety, designed with authority. Drafts, never sends.
            </div>
          </div>

          {ALLOW_SIGNUP && (
            <div className="seg" style={{ marginBottom: 22 }} data-testid="auth-toggle">
              <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")} data-testid="tab-login">Sign in</button>
              <button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")} data-testid="tab-register">Create account</button>
            </div>
          )}

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {mode === "register" && (
              <>
                <label className="text-dim" style={{ fontSize: 12 }}>
                  Your name
                  <input
                    className="field"
                    style={{ marginTop: 6 }}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tyrre"
                    data-testid="name-input"
                  />
                </label>
                <label className="text-dim" style={{ fontSize: 12 }}>
                  Your assistant&apos;s name
                  <input
                    className="field"
                    style={{ marginTop: 6 }}
                    value={assistantName}
                    onChange={(e) => setAssistantName(e.target.value)}
                    placeholder="Andre"
                    data-testid="assistant-name-input"
                  />
                </label>
              </>
            )}
            <label className="text-dim" style={{ fontSize: 12 }}>
              Email
              <input
                className="field"
                style={{ marginTop: 6 }}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="email-input"
                autoComplete="email"
                required
              />
            </label>
            <label className="text-dim" style={{ fontSize: 12 }}>
              Password
              <input
                className="field"
                style={{ marginTop: 6 }}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
              />
            </label>

            <button className="btn btn-primary" type="submit" disabled={busy} data-testid="submit-btn"
              style={{ justifyContent: "center", padding: "12px 16px", marginTop: 8 }}>
              {busy ? "Just a moment…" : mode === "login" ? "Sign in" : "Activate my workspace"}
            </button>
          </form>

          {(SHOW_GOOGLE || SHOW_MICROSOFT) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: "var(--hairline)" }} />
              <span className="text-dim" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "var(--hairline)" }} />
            </div>
          )}

          {SHOW_GOOGLE && (
            <button onClick={handleGoogle} className="btn" data-testid="google-signin-btn"
              style={{ width: "100%", justifyContent: "center", padding: "11px 14px" }}>
              <svg width="16" height="16" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.2 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.3 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.3 29 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 43.5c5 0 9.4-1.7 12.9-4.6l-6-5.1C28.8 35 26.5 35.5 24 35.5c-5.1 0-9.6-3.1-11.3-7.4l-6.6 5.1C9.4 39.1 16 43.5 24 43.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.4 5.8l6 5.1c-.4.3 6.6-4.8 6.6-14.9 0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
          )}

          {SHOW_MICROSOFT && (
            <button onClick={handleMicrosoft} className="btn" data-testid="microsoft-signin-btn"
              style={{ width: "100%", justifyContent: "center", padding: "11px 14px", marginTop: 10 }}>
              <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Continue with Microsoft
            </button>
          )}

          <div className="text-dim" style={{ marginTop: 22, fontSize: 12, lineHeight: 1.6, textAlign: "center" }}>
            Drafts and proposes — never sends without your word. Your vault stays yours.
          </div>
        </div>
      </div>
    </div>
  );
}
